import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';
import { MaskedLines, Reveal } from './reveal';

/**
 * HammerInAction — "Watch it drive" section.
 *
 * Portrait video carousel (mobile-first) — most visitors will be on phones,
 * so we frame the videos as 9:16 clips centered in the viewport and let
 * the carousel handle swapping between multiple action shots.
 *
 * Add more clips by pushing entries into VIDEOS. Each entry is:
 *   { src: '/assets/hammer_action.mp4', caption: 'optional short label' }
 */
const VIDEOS = [
  {
    src: '/assets/hammer_action_8.mp4',
    caption: 'The whole operation from above',
  },
  {
    src: '/assets/hammer_action_7.mp4',
    caption: 'Concrete piling sinks into the lakebed',
  },
  {
    src: '/assets/hammer_action_6.mp4',
    caption: 'Close-up: driving a wood piling home',
  },
  {
    src: '/assets/hammer_action_5.mp4',
    caption: 'Pounding a piling home in the Florida sun',
  },
  {
    src: '/assets/hammer_action_4.mp4',
    caption: 'Water flies as the piling drives deep',
  },
  {
    src: '/assets/hammer_action_3.mp4',
    caption: 'Rigged up and engaging the drive',
  },
  {
    src: '/assets/hammer_action_2.mp4',
    caption: 'Operator hits the control box — hammer engages',
  },
  {
    src: '/assets/hammer_action.mp4',
    caption: 'Absolute Hammer driving a piling',
  },
  {
    src: '/assets/hammer_action_0.mp4',
    caption: 'The Absolute Hammer driving a concrete piling',
  },
];

export default function HammerInAction() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const videoRef = useRef(null);

  const clip = VIDEOS[index];
  const hasMultiple = VIDEOS.length > 1;

  const goto = (i) => {
    const next = (i + VIDEOS.length) % VIDEOS.length;
    setIndex(next);
    // small delay so React swaps the src before we call play()
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(() => {});
        setPaused(false);
      }
    }, 20);
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
      setPaused(false);
    } else {
      v.pause();
      setPaused(true);
    }
  };

  return (
    <section
      id="hammer-in-action"
      data-testid="hammer-in-action-section"
      className="relative bg-black border-t border-white/5 py-16 sm:py-20 lg:py-28 overflow-hidden"
    >
      <div className="relative max-w-6xl mx-auto px-5 sm:px-6 lg:px-10">
        {/* Header */}
        <div className="max-w-3xl">
          <Reveal>
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <span className="h-px w-8 sm:w-10 bg-cyan-400" />
              <span className="font-tech text-cyan-300 text-[10px] sm:text-[11px] uppercase tracking-[0.28em]">
                In Action
              </span>
            </div>
          </Reveal>
          <h2 className="font-display text-white text-3xl sm:text-4xl md:text-5xl font-bold uppercase leading-[1.02] tracking-tight">
            <MaskedLines
              delay={0.15}
              lines={[
                'Watch it',
                <span className="text-cyan-300">drive.</span>,
              ]}
            />
          </h2>
        </div>

        {/* Portrait video carousel */}
        <Reveal delay={0.25} className="mt-10 sm:mt-14 flex justify-center">
          <div
            className="relative w-full max-w-[360px] sm:max-w-[400px] md:max-w-[440px] rounded-xl overflow-hidden border border-white/10 bg-[#050608] shadow-2xl shadow-cyan-500/5"
            data-testid="hammer-in-action-carousel"
          >
            {/* Portrait aspect frame — approx 9:16 to match phone-shot source */}
            <div className="relative aspect-[9/16]">
              <video
                key={clip.src}
                ref={videoRef}
                src={clip.src}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="hammer-action-video"
              />

              {/* Play/pause overlay button (bottom-left) */}
              <button
                type="button"
                onClick={togglePlay}
                aria-label={paused ? 'Play video' : 'Pause video'}
                data-testid="hammer-action-play-toggle"
                className="absolute bottom-3 left-3 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm hover:bg-black/80 border border-white/20 flex items-center justify-center text-white/90 hover:text-cyan-300 transition-colors"
              >
                {paused ? (
                  <Play className="w-4 h-4 ml-0.5" fill="currentColor" />
                ) : (
                  <Pause className="w-4 h-4" fill="currentColor" />
                )}
              </button>

              {/* Slide counter (bottom-right) — only when there are multiple */}
              {hasMultiple && (
                <div
                  className="absolute bottom-3 right-3 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm border border-white/20 font-tech text-white/90 text-[11px] tracking-[0.14em] uppercase"
                  data-testid="hammer-action-counter"
                >
                  {index + 1} / {VIDEOS.length}
                </div>
              )}

              {/* Prev/Next arrows — only when there are multiple */}
              {hasMultiple && (
                <>
                  <button
                    type="button"
                    onClick={() => goto(index - 1)}
                    aria-label="Previous video"
                    data-testid="hammer-action-prev"
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm hover:bg-black/80 border border-white/20 flex items-center justify-center text-white/90 hover:text-cyan-300 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => goto(index + 1)}
                    aria-label="Next video"
                    data-testid="hammer-action-next"
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm hover:bg-black/80 border border-white/20 flex items-center justify-center text-white/90 hover:text-cyan-300 transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>

            {/* Optional caption below the frame */}
            {clip.caption && (
              <div className="px-4 py-3 border-t border-white/5 text-center">
                <span className="font-tech text-zinc-400 text-[10px] sm:text-[11px] uppercase tracking-[0.24em]">
                  {clip.caption}
                </span>
              </div>
            )}
          </div>
        </Reveal>

        {/* Dot indicators — only when there are multiple */}
        {hasMultiple && (
          <Reveal delay={0.4} className="mt-6 flex justify-center gap-2">
            <div className="flex justify-center gap-2" data-testid="hammer-action-dots">
              {VIDEOS.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => goto(i)}
                  aria-label={`Go to video ${i + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === index
                      ? 'w-8 bg-cyan-400'
                      : 'w-2 bg-white/25 hover:bg-white/50'
                  }`}
                />
              ))}
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
